import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  TrendingUp,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function KpiCard({ kpi, i }) {
  const up = kpi.trend === "up";
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      initial="hidden"
      animate="show"
      className="relative bg-[#121214] border border-white/5 p-5 group hover:border-white/15 transition-colors"
      data-testid={`kpi-${kpi.key}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {kpi.label}
        </span>
        <span className={`flex items-center gap-1 font-mono-apex text-[11px] ${up ? "text-emerald-400" : "text-[#dc2626]"}`}>
          {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {kpi.delta}
        </span>
      </div>
      <div className="font-mono-apex text-3xl text-white tracking-tight">{kpi.value}</div>
      <div className={`absolute left-0 top-0 bottom-0 w-[2px] ${up ? "bg-emerald-400/40" : "bg-[#dc2626]/60"}`} />
    </motion.div>
  );
}

const ICONS = { deal: TrendingUp, signal: AlertTriangle, ai: Sparkles };
const SEV_COLORS = { success: "text-emerald-400", warning: "text-[#dc2626]", info: "text-zinc-300" };

export default function DashboardOverview() {
  const [kpis, setKpis] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [k, r, a] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/dashboard/revenue"),
          api.get("/dashboard/activity"),
        ]);
        setKpis(k.data.kpis);
        setRevenue(r.data.series);
        setActivity(a.data.items);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">briefing · today</div>
        <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight">
          Welcome back. <span className="silver-text italic">Storm watch is online.</span>
        </h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpis.map((k, i) => (
          <KpiCard key={k.key} kpi={k} i={i} />
        ))}
      </div>

      {/* Chart + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#121214] border border-white/5 p-5" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                revenue · 12-month telemetry
              </div>
              <div className="font-display text-2xl text-white">Realised vs Forecast</div>
            </div>
            <div className="flex items-center gap-4 font-mono-apex text-[10px] uppercase tracking-[0.2em]">
              <span className="flex items-center gap-2 text-zinc-300">
                <span className="w-2 h-2 bg-[#dc2626]" /> jobs revenue
              </span>
              <span className="flex items-center gap-2 text-zinc-300">
                <span className="w-2 h-2 bg-white/60" /> forecast
              </span>
            </div>
          </div>

          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#dc2626" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="fc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0b",
                    border: "1px solid rgba(255,255,255,0.1)",
                    fontFamily: "JetBrains Mono",
                    fontSize: 11,
                  }}
                  labelStyle={{ color: "#a1a1aa" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Area type="monotone" dataKey="forecast" stroke="rgba(255,255,255,0.6)" strokeDasharray="4 4" fill="url(#fc)" strokeWidth={1.5} />
                <Area type="monotone" dataKey="revenue" stroke="#dc2626" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-[#121214] border border-white/5 p-5" data-testid="activity-feed">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
            live activity
          </div>
          <ul className="space-y-4 max-h-[330px] overflow-y-auto scroll-thin pr-2">
            {activity.map((a, i) => {
              const Icon = ICONS[a.type] || Activity;
              return (
                <li key={i} className="flex gap-3">
                  <span className={`mt-0.5 ${SEV_COLORS[a.severity]}`}>
                    <Icon size={14} strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-body text-sm text-white truncate">{a.title}</div>
                    <div className="font-mono-apex text-[10px] text-zinc-500 mt-0.5">{a.detail}</div>
                  </div>
                  <span className="font-mono-apex text-[10px] text-zinc-600 whitespace-nowrap">{a.time}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Signal Table */}
      <div className="bg-[#121214] border border-white/5 overflow-hidden" data-testid="signals-table">
        <div className="px-5 pt-5">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            recent intelligence signals
          </div>
          <div className="font-display text-xl text-white mb-4">Top-ranked moves · last 24h</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full font-mono-apex text-xs">
            <thead>
              <tr className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] border-y border-white/5">
                <th className="text-left px-5 py-3 font-normal">Signal</th>
                <th className="text-left px-5 py-3 font-normal">Domain</th>
                <th className="text-left px-5 py-3 font-normal">Confidence</th>
                <th className="text-left px-5 py-3 font-normal">Recommended</th>
                <th className="text-right px-5 py-3 font-normal">Detected</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Tornado track entering job zone", "Storm", "94%", "Pre-position 4 crews to OK", "12m"],
                ["Hail track confirmed · Denver Metro", "Storm", "87%", "Open intake line · 3,400 homes", "38m"],
                ["Crew capacity tight · 18% over", "Ops", "82%", "Activate reserve crews 8–11", "1h"],
                ["Hurricane Cat-2 landfall in 18h", "Storm", "76%", "Stage equipment to FL panhandle", "2h"],
                ["Insurance backlog · 14 claims", "Claims", "71%", "Expedite carrier-3 packets", "4h"],
              ].map((row, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-white">{row[0]}</td>
                  <td className="px-5 py-3 text-zinc-300">{row[1]}</td>
                  <td className="px-5 py-3 text-[#dc2626]">{row[2]}</td>
                  <td className="px-5 py-3 text-zinc-300">{row[3]}</td>
                  <td className="px-5 py-3 text-right text-zinc-500">{row[4]} ago</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
